import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, api, type Uuid } from "@shared/api";
import { SLOWMODES } from "@shared/config";
import { plainText } from "@shared/lib/markup";
import { Field, SettingsModal } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { MenuPlate } from "@shared/ui/menu-plate";
import { Card, Row } from "@shared/ui/settings-card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";
import { IconSettings, IconThreads, IconTrash } from "@shared/ui/icons";

/** Сколько знаков в имени ветки — как у сервера. */
export const TITLE_MAX = 100;

/** «Скрыть после неактивности» — сроки Discord, в минутах. */
const QUIET: { minutes: number; label: string }[] = [
  { minutes: 60, label: "1 час" },
  { minutes: 1440, label: "24 часа" },
  { minutes: 4320, label: "3 дня" },
  { minutes: 10080, label: "1 неделя" },
];

interface Draft {
  name: string;
  slowmode: number;
  quiet: number;
}

/**
 * «Редактировать ветку» — как в Discord: окно настроек с разделом «Обзор»
 * (название, медленный режим, «скрыть после неактивности») и «Удалить ветку»
 * внизу колонки. Сохраняется плашкой снизу, как настройки канала.
 *
 * Значения берутся свежими у сервера при открытии: меню зовут со строки,
 * которая знает о ветке не всё (у строки под каналом нет ни срока тишины, ни
 * медленного режима).
 *
 * Медленный режим и удаление — только тем, кто управляет сообщениями; автор
 * корня правит имя и срок тишины.
 */
export function ThreadSettings({
  channelId,
  threadId,
  canManage,
  canDelete,
  onChanged,
  onDelete,
  onClose,
}: {
  channelId: Uuid;
  threadId: Uuid;
  canManage: boolean;
  canDelete: boolean;
  /** Сохранилось — перечитать ветку там, где она видна. */
  onChanged: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const { data: thread } = useQuery({
    queryKey: ["thread-settings", threadId],
    queryFn: () => api.threads.get(channelId, threadId),
    staleTime: 0,
  });
  const [initial, setInitial] = useState<Draft | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!thread || initial) return;
    const loaded = {
      name: thread.title ?? "",
      slowmode: thread.slowmode ?? 0,
      quiet: thread.quiet_after ?? 10080,
    };
    setInitial(loaded);
    setDraft(loaded);
  }, [thread, initial]);

  const changes =
    initial && draft
      ? [draft.name.trim() !== initial.name.trim(), draft.slowmode !== initial.slowmode, draft.quiet !== initial.quiet].filter(
          Boolean,
        ).length
      : 0;

  async function save() {
    if (!initial || !draft) return;
    setBusy(true);
    setProblem(null);
    try {
      if (draft.name.trim() !== initial.name.trim()) {
        await api.threads.title(channelId, threadId, draft.name.trim() || null);
      }
      if (draft.slowmode !== initial.slowmode) await api.threads.slowmode(channelId, threadId, draft.slowmode);
      if (draft.quiet !== initial.quiet) await api.threads.quiet(channelId, threadId, draft.quiet);
      const saved = { ...draft, name: draft.name.trim() };
      setInitial(saved);
      setDraft(saved);
      onChanged();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Не сохранилось");
    } finally {
      setBusy(false);
    }
  }

  // Своего имени нет — ветку зовут началом корня, как в списках.
  const shown = thread?.title || plainText(thread?.root?.content ?? "").slice(0, 60) || "Ветка";
  const slowmodes = SLOWMODES.some((item) => item.seconds === draft?.slowmode)
    ? SLOWMODES
    : [...SLOWMODES, { seconds: draft?.slowmode ?? 0, label: `${draft?.slowmode ?? 0} с` }];

  return (
    <SettingsModal
      title="Обзор"
      subtitle="Название, медленный режим и когда ветка скроется"
      size="wide"
      dirty={changes > 0}
      onClose={onClose}
      nav={
        <nav className="chan-settings-nav set-nav-wide" aria-label="Разделы настроек ветки">
          <MenuPlate match=".chan-settings-tab.on" className="nav-plate" />
          <div className="chan-settings-title ellipsis">
            <IconThreads size={17} /> {shown}
          </div>
          <button type="button" className="chan-settings-tab on" aria-current="page">
            <IconSettings size={16} />
            Обзор
          </button>
          <span className="grow" />
          {canDelete && (
            <>
              <hr className="chan-settings-sep" />
              <button type="button" className="chan-settings-danger" onClick={onDelete}>
                <IconTrash size={16} />
                Удалить ветку
              </button>
            </>
          )}
        </nav>
      }
    >
      <div className="set-scroll set-under-bar">
        <div className="set-cards">
          {draft ? (
            <Card title="Ветка">
              <Field label="Название ветки">
                <Input
                  value={draft.name}
                  maxLength={TITLE_MAX}
                  placeholder="Пусто — звать началом сообщения"
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              </Field>
              {canManage && (
                <>
                  <hr className="set-sep" />
                  <Row
                    title="Медленный режим"
                    note="Как часто один человек может писать в ветке. У ветки он свой, канал его не задаёт. Тех, у кого право обхода, не держит."
                  >
                    <Select
                      value={String(draft.slowmode)}
                      onValueChange={(value) => setDraft({ ...draft, slowmode: Number(value) })}
                    >
                      <SelectTrigger className="w-[170px] shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {slowmodes.map((item) => (
                          <SelectItem key={item.seconds} value={String(item.seconds)}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Row>
                </>
              )}
              <hr className="set-sep" />
              <Row
                title="Скрыть после неактивности"
                note="Ветка перестанет показываться в списке каналов, когда в ней столько молчат."
              >
                <Select value={String(draft.quiet)} onValueChange={(value) => setDraft({ ...draft, quiet: Number(value) })}>
                  <SelectTrigger className="w-[170px] shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUIET.map((item) => (
                      <SelectItem key={item.minutes} value={String(item.minutes)}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Row>
            </Card>
          ) : (
            <div className="small muted">Загружаю ветку…</div>
          )}
          {problem && <div className="small danger-text">{problem}</div>}
        </div>
      </div>

      <div className={`chan-settings-foot surface-pop ${changes ? "dirty" : ""}`}>
        <span className="grow">Изменений · {changes}</span>
        <Button
          variant="secondary"
          size="sm"
          type="button"
          disabled={!changes || busy}
          onClick={() => initial && setDraft(initial)}
        >
          Сбросить
        </Button>
        <Button size="sm" type="button" disabled={!changes || busy} onClick={() => void save()}>
          {busy ? "Секунду…" : "Сохранить"}
        </Button>
      </div>
    </SettingsModal>
  );
}
